import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  BooleanFilterControl,
  CombinationFilter,
  CombinationFilterOption,
  EnumFilterControl,
  formatFilterNumber,
  groupCombinationFilterOptions,
  RangeFilterControl
} from 'components/common/combination-filter-controls';
import Modal from 'components/common/modal';
import RecommendationsStrip from 'components/common/recommendations-strip';
import Button from 'components/inputs/button';
import ToggleSwitch from 'components/inputs/toggle-switch';
import AccessorySelectionModal from 'components/layout/modals/accessory-selection-modal';
import ExistingEquipmentComponentEditor from 'components/layout/modals/existing-equipment-component-editor';
import { existingSystemComponentTypeProperties } from 'data/constants';
import { useUpdateComponentReplacingMutation } from 'features/api/project-api';
import { updateExistingSystemComponent } from 'features/structure';
import useQuotableAdd from 'hooks/use-quotable-add';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'utils/api';
import { basicProductTitle } from 'utils/basic-product-title';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';
import {
  CcbBand,
  classifyCcbCoolingContext,
  resolveCcbBands
} from 'utils/ccb-band-logic';
import {
  computeCombinationTotalPrice,
  parseAccessoriesString
} from 'utils/combination-pricing';
import { abbreviateNumber, formatPrice } from 'utils/helpers';
import { formatKeptSystemTypeLabel } from 'utils/kept-system-type-annotation';
import {
  buildSelectedCombination,
  canSelectCombination,
  SelectedCombination
} from 'utils/quotable-add-pipeline';
import {
  buildComponentsKey,
  isRecommendationsCheckboxDisabled,
  isUnanchoredWithoutCodes,
  recommendedAllTypesCount,
  reconcileRecommendedSubset,
  reconcileSubsetAgainstFacet,
  resolveEffectiveSystemTypeCodes,
  resolveRecommendationsSettle,
  shouldDeferCombinationsFetch
} from 'utils/recommendations-strip-logic';
import { productImageUrl } from 'utils/system-products';
import { resolveTonnageSeed } from 'utils/tonnage-logic';

type CompatibleCombinationsBrowserModalProps = {
  openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  fetchParams: {
    systemUniqueId: string;
    activeOnly: boolean;
    inStockOnly: boolean;
    matchingMode: 'general' | 'exact';
  } | null;
};

// Light product enrichment returned by the backend per real (non-Generic)
// model on the current page, keyed "manufacturer|modelNumber".
export type BasicProductData = {
  productId: number;
  manufacturer: string;
  modelNumber: string;
  name: string;
  description: string | null;
  shortDescription?: string | null;
  price: number | null;
  isOnSale: boolean;
  productUrl: string | null;
  photo: string | null;
  fulfillableStock: number;
  active: boolean;
};

// Resolved + enriched accessory returned by the backend, keyed by the
// `"accessoryId*typeId"` pair as it appears in each combination's
// STRING_AGG Accessories column. `product` is null when the linked
// AlpineProductID isn't in Products (e.g. inactive or deleted).
export type EnrichedAccessory = {
  accessoryTypeCode: string;
  accessoryTypeLabel: string;
  alpineProductId: number;
  manufacturer: string;
  modelNumber: string;
  heatingCapacityBtu?: number | null;
  product: BasicProductData | null;
};

// Perf values arrive from pnet as numbers, numeric strings, or null.
// Purely-decimal strings only: Number('') is 0 and Number('0x10') is 16,
// so a bare Number()+isFinite check would mangle empty/exotic strings.
const DECIMAL_STRING_RE = /^-?\d+(\.\d+)?$/;

export function formatPerfValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number' && Number.isFinite(value))
    return formatFilterNumber(value);
  if (typeof value === 'string' && DECIMAL_STRING_RE.test(value.trim()))
    return formatFilterNumber(Number(value));
  return String(value);
}

export function formatSpecValue(value: unknown): string {
  if (value === true || value === 1) return 'Yes';
  if (value === false || value === 0) return 'No';
  if (value === null || value === undefined || value === '') return '—';
  return formatPerfValue(value);
}

// Band edges rounded to the nearest 100 BTU: combination capacities are
// whole BTUs at coarse granularity, so exact multiplication precision adds
// nothing but noisy input values.
function roundTo100(value: number): number {
  return Math.round(value / 100) * 100;
}

// Capacity band filters derived from the structure's BTU requirements
// ("Use structure requirements", AHA-6681; per-side bands AHA-6796):
// [btu × band.lower, btu × band.upper] per side, applied only to sides
// with a requirement > 0. The band rides the existing plain range filters,
// so the server pipeline is untouched and NULL-capacity rows are excluded
// by the normal range semantics.
export function buildStructureReqFilters(
  totalBTUs: { heating: number; cooling: number },
  bands: { cooling: CcbBand; heating: CcbBand }
): Array<Extract<CombinationFilter, { kind: 'range' }>> {
  const sides = [
    { field: 'CoolingCapacity', btu: totalBTUs.cooling, band: bands.cooling },
    { field: 'HeatingCapacity', btu: totalBTUs.heating, band: bands.heating }
  ];
  return sides
    .filter(({ btu }) => btu > 0)
    .map(({ field, btu, band }) => ({
      field,
      kind: 'range' as const,
      min: roundTo100(btu * band.lower),
      max: roundTo100(btu * band.upper)
    }));
}

function formatSpecLabel(key: string): string {
  return (
    key
      // Insert space before uppercase runs: "MinblowerCFM" → "Minblower CFM"
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      // Insert space before digits after letters: "R410A" → "R 410A"
      .replace(/([a-zA-Z])(\d)/g, '$1 $2')
      // Fix common patterns: "R-410 A" → "R-410A", "R-454 B" → "R-454B"
      .replace(/R-([\d]+)\s+([A-Z])\b/g, 'R-$1$2')
      .trim()
  );
}

function ModelSpecsTooltip({
  manufacturer,
  modelNumber,
  modelSpecs,
  product,
  children
}: {
  manufacturer: string | null | undefined;
  modelNumber: string | null | undefined;
  modelSpecs: Record<string, Record<string, unknown>>;
  product?: BasicProductData;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ fallbackPlacements: ['top-start', 'right', 'left'] }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ]
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  if (!modelNumber) return children;
  const key = `${manufacturer ?? ''}|${modelNumber}`;
  const specs = modelSpecs[key];

  const specEntries = specs
    ? Object.entries(specs).filter(
        ([, value]) => value !== null && value !== undefined
      )
    : [];

  // Render the tooltip when there is anything to show: product data OR specs.
  // Without either, fall through to plain children (no hover affordance).
  if (!product && specEntries.length === 0) return children;

  const specsTitle =
    manufacturer === 'Generic' ? 'Generic Model Specs' : 'Model Specs';
  const inStock = product != null && product.fulfillableStock > 0;
  const productTitle = product
    ? basicProductTitle(product, product.name)
    : null;

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className='cursor-help underline decoration-dotted underline-offset-2'
      >
        {children}
      </span>
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className='z-50 max-h-96 max-w-sm overflow-y-auto rounded border border-gray-200 bg-white p-3 shadow-lg'
          >
            {product && (
              <div className='mb-3 flex gap-3'>
                {product.photo && (
                  <img
                    src={productImageUrl(product.photo)}
                    alt={product.name}
                    className='h-20 w-20 flex-shrink-0 rounded border border-gray-200 object-contain'
                  />
                )}
                <div className='flex min-w-0 flex-col gap-1'>
                  <div className='line-clamp-2 text-sm font-semibold text-gray-900'>
                    {productTitle}
                  </div>
                  {productTitle !== product.name && (
                    <div className='text-xs text-gray-500'>{product.name}</div>
                  )}
                  {product.price != null && (
                    <div
                      className={`text-sm font-semibold ${
                        product.isOnSale ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {formatPrice(product.price)}
                      {product.isOnSale && (
                        <span className='ml-1 text-[10px] font-medium uppercase tracking-wider text-red-600'>
                          Sale
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    className={`text-xs font-medium ${
                      inStock ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {inStock ? 'In stock' : 'Out of stock'}
                  </div>
                  {product.productUrl && (
                    <a
                      href={`https://alpinehomeair.com${product.productUrl}`}
                      target='_blank'
                      rel='noreferrer'
                      className='text-xs font-semibold text-blue-600 hover:text-blue-800'
                    >
                      View product →
                    </a>
                  )}
                </div>
              </div>
            )}
            {specEntries.length > 0 && (
              <>
                <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-600'>
                  {specsTitle}
                </div>
                <table className='w-full text-xs'>
                  <tbody>
                    {specEntries.map(([fieldKey, value]) => (
                      <tr key={fieldKey}>
                        <td className='py-0.5 pr-3 align-top font-medium text-gray-500'>
                          {formatSpecLabel(fieldKey)}
                        </td>
                        <td className='py-0.5 text-right text-gray-900'>
                          {formatSpecValue(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <FloatingArrow
              fill='white'
              stroke='#e5e7eb'
              strokeWidth={1}
              ref={arrowRef}
              context={context}
            />
          </div>
        )}
      </FloatingPortal>
    </>
  );
}

// Inline indicator next to a unit's model name: green when in stock, red when
// out of stock, hidden when there is no product data (Generic placeholder or
// unmatched real model).
export function StockDot({ product }: { product?: BasicProductData }) {
  if (!product) return null;
  if (!product.active) {
    return (
      <span
        title='Inactive / discontinued'
        className='ml-1.5 inline-block h-2 w-2 rounded-full bg-gray-400 align-middle'
      />
    );
  }
  const inStock = product.fulfillableStock > 0;
  return (
    <span
      title={inStock ? 'In stock' : 'Out of stock'}
      className={`ml-1.5 inline-block h-2 w-2 rounded-full align-middle ${
        inStock ? 'bg-green-500' : 'bg-red-500'
      }`}
    />
  );
}

// Hover tooltip that lists every resolved accessory for a combination.
// Trigger is a small count badge; the body shows one row per accessory with
// type label, manufacturer + model, price (or em-dash), and a StockDot.
function AccessoriesTooltip({ entries }: { entries: EnrichedAccessory[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ fallbackPlacements: ['top-start', 'right', 'left'] }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ]
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className='cursor-help underline decoration-dotted underline-offset-2'
      >
        {entries.length}
      </span>
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className='z-50 max-h-96 max-w-md overflow-y-auto rounded border border-gray-200 bg-white p-3 shadow-lg'
          >
            <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-600'>
              Accessories
            </div>
            <table className='w-full text-xs'>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={`${entry.accessoryTypeCode}-${entry.alpineProductId}`}
                    className='align-top'
                  >
                    <td className='py-1 pr-3 font-medium text-gray-700'>
                      {entry.accessoryTypeLabel}
                    </td>
                    <td className='py-1 pr-3 text-gray-900'>
                      <span className='whitespace-nowrap'>
                        {[entry.manufacturer, entry.modelNumber]
                          .filter(Boolean)
                          .join(' ')}
                      </span>
                      <StockDot product={entry.product ?? undefined} />
                    </td>
                    <td className='whitespace-nowrap py-1 text-right text-gray-900'>
                      {entry.product && entry.product.price != null
                        ? formatPrice(entry.product.price)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FloatingArrow
              fill='white'
              stroke='#e5e7eb'
              strokeWidth={1}
              ref={arrowRef}
              context={context}
            />
          </div>
        )}
      </FloatingPortal>
    </>
  );
}

function SortHeader({
  label,
  field,
  sortBy,
  sortDirection,
  onSort
}: {
  label: string;
  field: string;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
}) {
  return (
    <th
      className='cursor-pointer select-none px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700'
      onClick={() => onSort(field)}
    >
      <span className='flex items-center gap-1'>
        {label}
        {sortBy === field && (
          <span className='text-blue-600'>
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </span>
    </th>
  );
}

export default function CompatibleCombinationsBrowserModal({
  openState,
  fetchParams
}: CompatibleCombinationsBrowserModalProps) {
  // Structure BTU requirements ("Use structure requirements", AHA-6681).
  // totalBTUs comes straight from Redux (btu-watcher keeps it current;
  // cooling already includes latent gain — no fetchParams change), or from
  // this system's System Design pair when one is linked. Band filters are
  // built only when the user toggles the control ON.
  const totalBTUs = useAppSelector((state) =>
    selectDuctedPairLoads(state, fetchParams?.systemUniqueId)
  );
  const ccbCapacityBandLowerBound = useAppSelector(
    (state) => state.data.ccbCapacityBandLowerBound
  );
  const ccbCapacityBandUpperMultiplier = useAppSelector(
    (state) => state.data.ccbCapacityBandUpperMultiplier
  );
  // Per-equipment-type sizing bands (AHA-6796); null keeps the legacy
  // uniform multipliers above as the live fallback (older API deploys).
  const sizingBands = useAppSelector((state) => state.data.sizingBands);
  // The project ZIP the server holds the results to the ProductRegulations
  // rules for (Low NOx / Ultra-Low NOx furnaces, ...). Sent explicitly so the
  // modal is filtered exactly like Choose Systems instead of relying on the
  // server's structure-address fallback; absent when the project has none.
  const projectZipCode = useAppSelector(
    (state) => state.project.locationData?.zipcode?.trim() || null
  );
  const hasStructureReqs = totalBTUs.heating > 0 || totalBTUs.cooling > 0;
  // Default OFF; the control is hidden entirely when the structure has no
  // BTU requirement. Re-initialized per system by the render-adjust block
  // below; otherwise the user's in-session choice persists across refetches
  // and universe changes.
  const [useStructureReqs, setUseStructureReqs] = useState(false);
  const [combinations, setCombinations] = useState<any[]>([]);
  const [modelSpecs, setModelSpecs] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [productData, setProductData] = useState<
    Record<string, BasicProductData>
  >({});
  const [accessoryData, setAccessoryData] = useState<
    Record<string, EnrichedAccessory>
  >({});
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [systemTypeCodes, setSystemTypeCodes] = useState<string[]>([]);
  // Recommendations-strip state (AHA-6681). `systemTypeCodes` above stays the
  // user's MANUAL button selection, untouched while the checkbox is on, so
  // unchecking restores it exactly; the fetch uses effectiveSystemTypeCodes.
  const [useRecommendations, setUseRecommendations] = useState(false);
  const [recommendedCodes, setRecommendedCodes] = useState<string[]>([]);
  // Narrowing within the recommended set while the checkbox is on. Empty =
  // ALL recommended (same convention as systemTypeCodes=[] = All Types).
  // When the recommendation context shifts, the subset is intersected against
  // the new recommendedCodes and normalized (reconcileRecommendedSubset) —
  // eagerly while the checkbox is on, lazily on re-check while it is off, so
  // a subset survives transient recommendation churn during an unchecked
  // stretch. Only a system switch clears it outright.
  const [recommendedSubset, setRecommendedSubset] = useState<string[]>([]);
  // In-flight window of the strip's recommendations refetch (AHA-6681
  // stale-codes race): while it's open and the checkbox is on, the fetch
  // effect must not pair fresh deps (kept set, page/sort) with the OLD
  // recommendedCodes the strip is about to replace. A ref + state pair:
  // the strip's child effect sets the ref synchronously so this modal's
  // fetch effect — running later in the SAME commit when their deps move
  // together — reads it and abstains; a setState alone would only commit
  // after that effect already ran. The state twin makes pending
  // render-observable (spinner / pagination), and
  // handleRecommendationsChange settles both when the codes land.
  const recsPendingRef = useRef(false);
  const [recsPending, setRecsPending] = useState(false);
  // Whether any fetch-effect run abstained during the window — the settle
  // path then forces exactly one replay (its deps may not move again on
  // their own: the kept toggle already consumed the dep change, and
  // identical codes keep the recommendedCodes/effectiveSystemTypeCodes
  // references still).
  const combosFetchDeferredRef = useRef(false);
  // Settle-replay trigger for the deferral above: alpine-client already had
  // a fetchKey for its retry buttons; here it exists solely so
  // handleRecommendationsChange can force the fetch effect to re-run when a
  // deferred run's deps won't move again on their own.
  const [fetchKey, setFetchKey] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [availableSystemTypes, setAvailableSystemTypes] = useState<
    { code: string; label: string; count: number }[]
  >([]);
  const [filters, setFilters] = useState<CombinationFilter[]>([]);
  const [filterOptions, setFilterOptions] = useState<CombinationFilterOption[]>(
    []
  );
  const [selectedCombinationIds, setSelectedCombinationIds] = useState<
    Set<number>
  >(new Set());
  // Default ON: only active (non-discontinued) equipment on fresh opens. The
  // state is mount-initialized, so a user's in-session toggle is preserved
  // across reopens rather than being overridden by the default.
  const [activeOnly, setActiveOnly] = useState(fetchParams?.activeOnly ?? true);
  // Default ON: only combinations whose purchasable components are all in
  // stock on fresh opens. Same mount-initialized pattern as activeOnly.
  const [inStockOnly, setInStockOnly] = useState(
    fetchParams?.inStockOnly ?? true
  );
  // In-card existing-equipment editing (AHA-6681): one card editable at a
  // time. `specsSaveNonce` bumps only after a spec edit's surgical PATCH
  // succeeds — the combinations query and the strip's context POST both
  // re-read PERSISTED data, so keying refetches on Redux spec values could
  // anchor stale specs (project-page edits persist behind a debounced
  // autosave). The nonce fires exactly when the DB is guaranteed current.
  const [editingComponentUniqueId, setEditingComponentUniqueId] = useState<
    string | null
  >(null);
  const [specsSaveNonce, setSpecsSaveNonce] = useState(0);

  const dispatch = useAppDispatch();
  const [updateComponentReplacing] = useUpdateComponentReplacingMutation();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const instanceUniqueId = useAppSelector(
    (state) => state.project.instanceUniqueId
  );
  const system = fetchParams
    ? existingSystems.find((s) => s.uniqueId === fetchParams.systemUniqueId)
    : undefined;
  const components = useMemo(() => system?.components ?? [], [system]);
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  // Same shared resolver on the same Redux inputs as the CCB-check
  // dispatchers (AHA-6796): forwarded into Phase 6 so the server can
  // compute tonnage-estimate ducted coverage.
  const addTonnageSeed = useMemo(
    () =>
      resolveTonnageSeed({
        record: system
          ? recommendationSelectionsBySystem[system.uniqueId]?.tonnage ?? null
          : null,
        components,
        coolingBtu: totalBTUs.cooling
      }),
    [system, recommendationSelectionsBySystem, components, totalBTUs.cooling]
  );
  // Shared add-to-quotable pipeline (AHA-6681 CS-4): Phase 5/6 POSTs,
  // client-side manufacture, addSelectedSystem dispatch, and the accessory
  // choice state machine all live in the hook/pipeline; the modal supplies
  // its own success side-effects (clear selection, close).
  const { beginAdd, accessorySelection } = useQuotableAdd({
    instanceUniqueId,
    components,
    existingSystemUniqueId: system?.uniqueId,
    accessoryData,
    tonnageSeed: addTonnageSeed,
    onSuccess: () => {
      setSelectedCombinationIds(new Set());
      openState[1](false);
    }
  });
  // Kept = components NOT being replaced. Sent to the backend so toggling
  // Replacing/Keeping re-runs the query against the current (Redux) state.
  const keptComponentUniqueIds = useMemo(
    () => components.filter((c) => !c.replacing).map((c) => c.uniqueId),
    [components]
  );
  // Kept-aware row titles (AHA-6681 display titles): when the kept set is
  // non-empty the fetch is kept-constrained, so rows render "Customer's
  // <segment>" via the formatter. The code is resolved from the facet's
  // label→code pairs (first match wins); unresolvable labels — and every
  // row while nothing is kept — render the raw SystemTypeLabel. The System
  // Type filter buttons and the add-flow names stay raw (single-set browse
  // context, no keptPairing).
  const keptComponentTypes = useMemo(
    () => components.filter((c) => !c.replacing).map((c) => c.type),
    [components]
  );
  const systemTypeCodeByLabel = useMemo(() => {
    const map = new Map<string, string>();
    availableSystemTypes.forEach((systemType) => {
      if (!map.has(systemType.label))
        map.set(systemType.label, systemType.code);
    });
    return map;
  }, [availableSystemTypes]);
  const displayRowSystemTypeLabel = (label: string) => {
    if (keptComponentUniqueIds.length === 0) return label;
    const code = systemTypeCodeByLabel.get(label);
    return code
      ? formatKeptSystemTypeLabel(label, code, keptComponentTypes)
      : label;
  };
  // Key for the strip's recommendation-context fetch — includes the replacing
  // flags because the refrigerant derivation prefers replacing components
  // over kept ones, and the specs-save nonce because spec edits change the
  // refrigerant/context derivation (the strip re-reads persisted data).
  const componentsKey = useMemo(
    () => buildComponentsKey(components, specsSaveNonce),
    [components, specsSaveNonce]
  );
  // While the checkbox is on (and codes exist) the recommended set drives the
  // combinations query; the manual selection stays untouched underneath.
  // resolveEffectiveSystemTypeCodes returns one of its input arrays and
  // handleRecommendationsChange keeps the previous recommendedCodes reference
  // when contents are unchanged, so this reference is stable enough to sit
  // directly in the fetch effect's deps.
  const effectiveSystemTypeCodes = useMemo(
    () =>
      resolveEffectiveSystemTypeCodes(
        useRecommendations,
        recommendedCodes,
        recommendedSubset,
        systemTypeCodes
      ),
    [useRecommendations, recommendedCodes, recommendedSubset, systemTypeCodes]
  );
  // The pnet cache universe (AHA-6681 universe scoping): the FULL recommended
  // bound, independent of the chip narrowing in effectiveSystemTypeCodes.
  // ON with zero codes falls back to the manual codes above, so no universe
  // is sent; OFF omits the key entirely.
  const universeCodes = useMemo(
    () =>
      useRecommendations && recommendedCodes.length > 0
        ? recommendedCodes
        : undefined,
    [useRecommendations, recommendedCodes]
  );

  // Active filter readers
  const rangeValueOf = (field: string): { min?: number; max?: number } => {
    const found = filters.find((f) => f.field === field && f.kind === 'range');
    return found && found.kind === 'range'
      ? { min: found.min, max: found.max }
      : {};
  };
  const enumValuesOf = (field: string): string[] => {
    const found = filters.find((f) => f.field === field && f.kind === 'enum');
    return found && found.kind === 'enum' ? found.values : [];
  };
  const booleanValueOf = (field: string): boolean | undefined => {
    const found = filters.find(
      (f) => f.field === field && f.kind === 'boolean'
    );
    return found && found.kind === 'boolean' ? found.value : undefined;
  };

  // Active filter writers (immutable upserts keyed by field)
  const setRangeFilter = (
    field: string,
    min: number | undefined,
    max: number | undefined
  ) => {
    setFilters((prev) => {
      const rest = prev.filter((f) => f.field !== field);
      if (min === undefined && max === undefined) return rest;
      return [...rest, { field, kind: 'range', min, max }];
    });
  };
  const toggleEnumFilter = (field: string, value: string) => {
    setFilters((prev) => {
      const existing = prev.find((f) => f.field === field && f.kind === 'enum');
      const current =
        existing && existing.kind === 'enum' ? existing.values : [];
      const nextValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const rest = prev.filter((f) => f.field !== field);
      if (nextValues.length === 0) return rest;
      return [...rest, { field, kind: 'enum', values: nextValues }];
    });
  };
  const setBooleanFilter = (field: string, value: boolean | undefined) => {
    setFilters((prev) => {
      const rest = prev.filter((f) => f.field !== field);
      if (value === undefined) return rest;
      return [...rest, { field, kind: 'boolean', value }];
    });
  };

  // Render-adjust reset for a system swap while the browser is open: all of
  // these states hold vocabulary derived from the OLD system's equipment
  // (manual systemTypeCodes, the strip's recommendedCodes/subset, faceted
  // filters, the per-system structure-requirements toggle), and the fetch
  // effect keys on fetchParams + effectiveSystemTypeCodes + filters — an
  // effect-based reset would let it fire once with (new systemUniqueId, old
  // filters/codes). Adjusting during render makes React restart the render
  // before commit, so the single post-commit fetch already sees the cleared
  // state on page 1. setPageNumber(1) is explicit: the reset usually changes
  // filters/effectiveSystemTypeCodes identity (whose pageNumber effect below
  // would reset it anyway, at the cost of a corrective refetch), but the old
  // system's codes and filters may already be empty — deps unchanged — while
  // the page is deep. setUseRecommendations(false) is safe to hand back to
  // the strip: it re-arms/auto-checks for the new system via its own intent
  // machinery once its context+recs fetches for the new id settle.
  // setSelectedCombinationIds lives here too so old-system combination IDs
  // can never survive a commit into the new system and leak into a beginAdd.
  // sortBy / sortDirection / activeOnly / inStockOnly / pageSize are
  // deliberately kept — view preferences, not session-scoped vocabulary (a
  // stale sort field is valid against any result set). Undefined transitions
  // (fetchParams not configured) are ordinary value changes (old → undefined
  // resets, undefined → new resets again idempotently), and the useState
  // initializer seeds prev with the mount-time id so the first render never
  // spuriously resets. Keyed on the system only: a totalBTUs/multiplier
  // change mid-session must not reset the user's in-session choices.
  const [prevSystemUniqueId, setPrevSystemUniqueId] = useState(
    fetchParams?.systemUniqueId
  );
  if (fetchParams?.systemUniqueId !== prevSystemUniqueId) {
    setPrevSystemUniqueId(fetchParams?.systemUniqueId);
    setSystemTypeCodes([]);
    setUseRecommendations(false);
    setRecommendedCodes([]);
    setRecommendedSubset([]);
    setFilters([]);
    setUseStructureReqs(false);
    setPageNumber(1);
    setSelectedCombinationIds(new Set());
  }

  // Clear multi-select when changing filters; pagination, sort, and
  // page-size changes intentionally preserve selections. System swaps clear
  // it in the render-adjust block above (this effect re-fires once after a
  // swap via the setFilters([]) identity change — a harmless redundant clear
  // of an already-empty set).
  useEffect(() => {
    setSelectedCombinationIds(new Set());
  }, [filters]);

  // Reset pageNumber when filter/sort/pageSize/kept set/persisted specs change
  useEffect(() => {
    setPageNumber(1);
  }, [
    pageSize,
    sortBy,
    sortDirection,
    effectiveSystemTypeCodes,
    universeCodes,
    keptComponentUniqueIds,
    specsSaveNonce,
    filters
  ]);

  // Close-reset for the pending window (AHA-6681 stale-codes race): this
  // component stays MOUNTED while closed (the Modal's Transition unmounts
  // only its children), so a mid-flight close unmounts the strip — its
  // cancelled POST never reports — while these refs/state survive. Without
  // this, a stale window would defer the next open's fetch until the
  // remounted strip's recommendations settle, and block a dep change made
  // while closed outright. Deliberately clears ONLY the pending window:
  // close must not change fetch deps or fire fetches (close/reopen touches
  // nothing — the session-swap suite pins that).
  const isModalOpen = openState[0];
  useEffect(() => {
    if (isModalOpen) return;
    recsPendingRef.current = false;
    setRecsPending(false);
    combosFetchDeferredRef.current = false;
  }, [isModalOpen]);

  // Fetch combinations when relevant state changes
  useEffect(() => {
    const fetchCombinations = async () => {
      if (!fetchParams) return;
      // Hold the fetch while the strip's recommendations refetch is in
      // flight and the checkbox narrows the search: this run would otherwise
      // query with the codes that POST is about to replace (AHA-6681
      // stale-codes race — page/sort/kept/active-only changes during the
      // window would pair fresh deps with the outgoing codes). Placed BEFORE
      // the unanchored check below: that check reads
      // effectiveSystemTypeCodes, so even the unanchored-empty decision
      // would be computed against stale data. handleRecommendationsChange
      // replays the fetch once the codes settle.
      if (
        shouldDeferCombinationsFetch(useRecommendations, recsPendingRef.current)
      ) {
        combosFetchDeferredRef.current = true;
        return;
      }
      // A run that proceeds closes over every current dep, so nothing
      // abstained earlier is still missing — reset the flag so a rare
      // mid-window uncheck doesn't buy a redundant settle refetch.
      combosFetchDeferredRef.current = false;
      // All equipment replacing with no system-type codes: the backend guards
      // this to empty (an unanchored search needs a type anchor), so skip the
      // POST and clear results; renderContent shows the explanatory copy.
      if (
        isUnanchoredWithoutCodes(
          keptComponentUniqueIds,
          effectiveSystemTypeCodes
        )
      ) {
        setCombinations([]);
        setModelSpecs({});
        setProductData({});
        setAccessoryData({});
        setTotalCount(0);
        setAvailableSystemTypes([]);
        setFilterOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.post(
          '/existing-equipment/compatible-combinations',
          {
            ...fetchParams,
            activeOnly,
            inStockOnly,
            keptComponentUniqueIds,
            systemTypeCodes: effectiveSystemTypeCodes,
            ...(universeCodes ? { universeCodes } : {}),
            pageNumber,
            pageSize,
            sortBy: sortBy || undefined,
            sortDirection: sortBy ? sortDirection : undefined,
            filters,
            // Server-side sizing bands opt-out (AHA-6796 T3): the browser
            // modal keeps its Phase-1 client-side band toggle, so it always
            // fetches the unbanded set and filters locally.
            applySizingBands: false,
            // Location regulations have no opt-out (they are law): the
            // project ZIP rides along so the server drops every combination
            // the cart would flag for it.
            ...(projectZipCode ? { zipCode: projectZipCode } : {})
          }
        );
        setCombinations(response.data.compatibleCombinations ?? []);
        setModelSpecs(response.data.modelSpecs ?? {});
        setProductData(response.data.productData ?? {});
        setAccessoryData(response.data.accessoryData ?? {});
        setTotalCount(response.data.metadata?.totalCount ?? 0);
        const facet = response.data.metadata?.systemTypes ?? [];
        setAvailableSystemTypes(facet);
        // Reconcile the live subset against the universe this response was
        // computed for: subset codes absent from the facet would filter the
        // query down to 0 results with no visible chip (the CCB trap), so
        // they are pruned (collapse to [] = all recommended when nothing
        // survives) and the widened fetch effect re-runs once — reference
        // stability in the helper makes the no-change case a setState
        // bail-out, never a refetch loop. Guarded on useRecommendations
        // because the subset is only live in the query while checked; the
        // stale-subset lazy reconcile on re-check stays the unchecked path.
        // useRecommendations is read from the closure of the render that
        // started this fetch: a mid-flight uncheck at worst reconciles a
        // dormant subset once — harmless, consistent with this fetch having
        // no cancellation.
        if (useRecommendations) {
          setRecommendedSubset((prev) =>
            reconcileSubsetAgainstFacet(prev, facet)
          );
        }
        setFilterOptions(response.data.filterOptions ?? []);
      } catch (error) {
        console.error('Failed to fetch combinations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCombinations();
    // useRecommendations only gates the facet reconcile above; it is listed
    // for exhaustive-deps but never changes effective fetch behavior on its
    // own — any toggle also swaps the effectiveSystemTypeCodes reference.
    // specsSaveNonce refires the query after an in-card spec edit persists
    // (the backend anchors kept components from the DB, so the PATCH must
    // land first — the save handler guarantees that ordering).
    // fetchKey is the settle-replay trigger: handleRecommendationsChange
    // bumps it when a deferred run must replay with otherwise-unchanged deps.
  }, [
    fetchParams,
    activeOnly,
    inStockOnly,
    keptComponentUniqueIds,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    effectiveSystemTypeCodes,
    universeCodes,
    useRecommendations,
    specsSaveNonce,
    fetchKey,
    filters,
    projectZipCode
  ]);

  const hasActiveFilters = filters.length > 0;
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const totalAvailable = availableSystemTypes.reduce(
    (sum, st) => sum + (st.count ?? 0),
    0
  );

  // Group faceted options into labeled sections, preserving the backend's
  // (registry) order of first appearance per group.
  const groupedOptions = useMemo(
    () => groupCombinationFilterOptions(filterOptions),
    [filterOptions]
  );

  // Renders a single filter control, or null when the facet has no data to
  // offer (so empty controls — and, by extension, empty groups — disappear).
  // While structure requirements drive a side's capacity filter, its inputs
  // are disabled and display the band (they sync from min/max). Sides with
  // no requirement stay enabled and user-controlled.
  const isStructureReqControlled = (field: string): boolean =>
    useStructureReqs &&
    ((field === 'CoolingCapacity' && totalBTUs.cooling > 0) ||
      (field === 'HeatingCapacity' && totalBTUs.heating > 0));

  const renderFilterControl = (option: CombinationFilterOption) => {
    if (option.kind === 'range') {
      // Empty facet (no min/max data) normally hides the control — EXCEPT
      // while structure requirements drive this side: the band is actively
      // filtering (often the very cause of the empty facet), so the disabled
      // inputs must stay visible showing it. Same never-invisible-filter
      // principle as the recommendedSubset facet reconcile. Ordinary
      // (user-editable) empty-facet behavior is unchanged.
      const structureReqControlled = isStructureReqControlled(option.field);
      if (!structureReqControlled && (option.min == null || option.max == null))
        return null;
      const value = rangeValueOf(option.field);
      return (
        <RangeFilterControl
          key={option.field}
          option={option}
          min={value.min}
          max={value.max}
          onCommit={(min, max) => setRangeFilter(option.field, min, max)}
          disabled={structureReqControlled}
        />
      );
    }
    if (option.kind === 'enum') {
      if (option.values.length === 0) return null;
      return (
        <EnumFilterControl
          key={option.field}
          option={option}
          selected={enumValuesOf(option.field)}
          onToggle={(value) => toggleEnumFilter(option.field, value)}
        />
      );
    }
    if (option.trueCount + option.falseCount === 0) return null;
    return (
      <BooleanFilterControl
        key={option.field}
        option={option}
        value={booleanValueOf(option.field)}
        onChange={(value) => setBooleanFilter(option.field, value)}
      />
    );
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // ON: overwrite the capacity range filters with the band for sides that
  // have a requirement (replacing any manual values); the other side stays
  // user-controlled. OFF: remove both capacity filters (inputs clear and
  // re-enable; manual values are NOT restored). The band snapshots at
  // toggle-ON time (AHA-6796) — like totalBTUs, a mid-session kept-set flip
  // keeps the old band until re-toggled.
  const handleToggleStructureReqs = () => {
    const next = !useStructureReqs;
    setUseStructureReqs(next);
    if (next) {
      const bands = resolveCcbBands({
        sizingBands,
        legacy: {
          lowerBound: ccbCapacityBandLowerBound,
          upperMultiplier: ccbCapacityBandUpperMultiplier
        },
        coolingContext: classifyCcbCoolingContext(components)
      });
      buildStructureReqFilters(totalBTUs, bands).forEach((f) =>
        setRangeFilter(f.field, f.min, f.max)
      );
    } else {
      setRangeFilter('CoolingCapacity', undefined, undefined);
      setRangeFilter('HeatingCapacity', undefined, undefined);
    }
  };

  const handleToggleUseRecommendations = () => {
    const next = !useRecommendations;
    // Re-check reconciles the subset held (possibly stale) through the
    // unchecked stretch against the then-current recommendedCodes: with
    // unchanged recommendations the same reference comes back (setState
    // bails out, no refetch); otherwise survivors-or-[] applies. Uncheck
    // touches nothing.
    if (next) {
      setRecommendedSubset((prev) =>
        reconcileRecommendedSubset(prev, recommendedCodes)
      );
    }
    setUseRecommendations(next);
  };

  // Called synchronously from the strip's fetch effect (child — runs before
  // this modal's fetch effect in the same commit) when a recommendations
  // refetch starts. The ref is the same-commit truth the fetch effect's
  // defer gate reads; the state twin drives the spinner.
  const handleRecommendationsPending = () => {
    recsPendingRef.current = true;
    setRecsPending(true);
  };

  const handleRecommendationsChange = ({
    matched,
    recommendedCodes: codes
  }: {
    matched: boolean;
    recommendedCodes: string[];
  }) => {
    // Settle the pending window FIRST, so the replay below batches with the
    // code/subset-reconcile/force-uncheck updates and at most ONE fetch
    // fires — with fresh codes AND fresh deps (the whole point of the
    // deferral). The reconcileRecommendedSubset call below stays where it
    // is: the batched replay observes the reconciled subset (same commit).
    const settle = resolveRecommendationsSettle(
      recsPendingRef.current,
      combosFetchDeferredRef.current
    );
    if (settle !== 'none') {
      recsPendingRef.current = false;
      setRecsPending(false);
      if (settle === 'refetch') {
        // A fetch abstained during the window and its deps may not move
        // again on their own (identical codes keep the
        // effectiveSystemTypeCodes reference still) — force the replay.
        combosFetchDeferredRef.current = false;
        setFetchKey((k) => k + 1);
      }
    }
    // Keep the previous array reference when the contents are identical —
    // the strip reports on every recommendations response, and a fresh
    // reference here would re-trigger the combinations fetch.
    const contentChanged =
      recommendedCodes.length !== codes.length ||
      recommendedCodes.some((c, i) => c !== codes[i]);
    if (contentChanged) {
      setRecommendedCodes(codes);
      // Reconcile the subset only while the checkbox is on (it is live in
      // the fetch). While unchecked the stale subset is deliberately left
      // untouched — handleToggleUseRecommendations reconciles it lazily on
      // re-check, so it survives transient churn (and a force-uncheck below).
      if (useRecommendations) {
        setRecommendedSubset((prev) => reconcileRecommendedSubset(prev, codes));
      }
    }
    // Force-uncheck when the checkbox becomes unusable (no matched scenario
    // or zero mapped codes) so the search silently falls back to the manual
    // button selection.
    if (isRecommendationsCheckboxDisabled(matched, codes)) {
      setUseRecommendations(false);
    }
  };

  // Generic placeholders intentionally never resolve; absent entries return
  // undefined so callers can fall back to plain text / no stock dot.
  const lookupProduct = (
    manufacturer: string | null | undefined,
    modelNumber: string | null | undefined
  ): BasicProductData | undefined => {
    if (!manufacturer || !modelNumber || manufacturer === 'Generic') {
      return undefined;
    }
    return productData[`${manufacturer}|${modelNumber}`];
  };

  // Accessory lookup: pairs that have no AlpineProductLink are absent from
  // `accessoryData`, so callers should treat undefined as "not a quotable
  // accessory" (skip silently in totals, omit from tooltip).
  const lookupAccessory = (
    accessoryId: number,
    typeId: number
  ): EnrichedAccessory | undefined => accessoryData[`${accessoryId}*${typeId}`];

  // Returns either the summed total ($X,XXX), 'Price unavailable' when a real
  // model is missing product data, or '—' when no real models are present
  // (e.g. a kept-only system with all Generic positions). The math lives in
  // utils/combination-pricing (shared with the CS-2 ducted cards).
  const renderTotalPrice = (combo: any): React.ReactNode => {
    const result = computeCombinationTotalPrice(
      combo,
      productData,
      accessoryData
    );
    if (result.kind === 'none') {
      return <span className='text-gray-400'>—</span>;
    }
    if (result.kind === 'unavailable') {
      return <span className='text-gray-400'>Price unavailable</span>;
    }
    return <span className='font-semibold'>{formatPrice(result.total)}</span>;
  };

  // Shaped payload for the Phase 5 bulk-add endpoint (mapping delegated to
  // the shared pipeline): Generic positions are dropped to null; real
  // positions carry the productId resolved by Phase 3.
  const selectedCombinations: SelectedCombination[] = combinations
    .filter((c) => selectedCombinationIds.has(c.ID))
    .map((c) => buildSelectedCombination(c, productData));

  // The defer window counts as loading (AHA-6681 stale-codes race): the
  // table currently on screen is settled against the OUTGOING codes, and
  // painting it as settled (or paginating it) would expose the
  // wrong-filtered result the deferral exists to prevent. Computed once —
  // renderContent and both pagination buttons share it.
  const combosDeferred = shouldDeferCombinationsFetch(
    useRecommendations,
    recsPending
  );

  const renderContent = () => {
    if (loading || combosDeferred) {
      return <div className='py-8 text-center text-gray-500'>Loading...</div>;
    }

    if (combinations.length === 0) {
      // Unanchored-without-codes: availableSystemTypes is empty here so the
      // manual type buttons don't render — the recommendations checkbox (or
      // flipping a component to Keeping) is the only path forward.
      if (
        isUnanchoredWithoutCodes(
          keptComponentUniqueIds,
          effectiveSystemTypeCodes
        )
      ) {
        return (
          <div className='py-8 text-center text-gray-500'>
            Mark equipment as Keeping or select recommended system types to see
            combinations.
          </div>
        );
      }
      return (
        <div className='py-8 text-center text-gray-500'>No results found.</div>
      );
    }

    return (
      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-3 py-2 text-left'>
                <input
                  type='checkbox'
                  aria-label='Select all on page'
                  checked={
                    combinations.some((c) =>
                      canSelectCombination(c, productData)
                    ) &&
                    combinations
                      .filter((c) => canSelectCombination(c, productData))
                      .every((c) => selectedCombinationIds.has(c.ID))
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCombinationIds((prev) => {
                        const next = new Set(prev);
                        combinations.forEach((c) => {
                          if (canSelectCombination(c, productData))
                            next.add(c.ID);
                        });
                        return next;
                      });
                    } else {
                      setSelectedCombinationIds((prev) => {
                        const next = new Set(prev);
                        combinations.forEach((c) => next.delete(c.ID));
                        return next;
                      });
                    }
                  }}
                />
              </th>
              <SortHeader
                label='System Type'
                field='SystemTypeLabel'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='Outdoor Unit'
                field='OutdoorModelNumber'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='Indoor Unit'
                field='IndoorModelNumber'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='Blower'
                field='BlowerModelNumber'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='SEER2'
                field='SEER2'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='EER2'
                field='EER2'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='HSPF2'
                field='HSPF2'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='COP5'
                field='COP5'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='AFUE'
                field='AFUE'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='Cooling Cap'
                field='CoolingCapacity'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortHeader
                label='Heating Cap'
                field='HeatingCapacity'
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className='px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                AHRI Cert
              </th>
              <th className='px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Accessories
              </th>
              <th className='px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Total Price
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {combinations.map((combo: any, idx: number) => {
              const selectable = canSelectCombination(combo, productData);
              return (
                <tr
                  key={combo.ID ?? idx}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className='whitespace-nowrap px-3 py-2'>
                    <input
                      type='checkbox'
                      aria-label='Select combination'
                      checked={selectedCombinationIds.has(combo.ID)}
                      disabled={!selectable}
                      title={
                        selectable
                          ? undefined
                          : 'Product not available in catalog'
                      }
                      onChange={() => {
                        setSelectedCombinationIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(combo.ID)) next.delete(combo.ID);
                          else next.add(combo.ID);
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {displayRowSystemTypeLabel(combo.SystemTypeLabel)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {combo.OutdoorModelNumber ? (
                      <>
                        <ModelSpecsTooltip
                          manufacturer={combo.OutdoorManufacturer}
                          modelNumber={combo.OutdoorModelNumber}
                          modelSpecs={modelSpecs}
                          product={lookupProduct(
                            combo.OutdoorManufacturer,
                            combo.OutdoorModelNumber
                          )}
                        >
                          {[combo.OutdoorManufacturer, combo.OutdoorModelNumber]
                            .filter(Boolean)
                            .join(' ')}
                        </ModelSpecsTooltip>
                        <StockDot
                          product={lookupProduct(
                            combo.OutdoorManufacturer,
                            combo.OutdoorModelNumber
                          )}
                        />
                      </>
                    ) : (
                      [combo.OutdoorManufacturer, combo.OutdoorModelNumber]
                        .filter(Boolean)
                        .join(' ')
                    )}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {combo.IndoorModelNumber ? (
                      <>
                        <ModelSpecsTooltip
                          manufacturer={combo.IndoorManufacturer}
                          modelNumber={combo.IndoorModelNumber}
                          modelSpecs={modelSpecs}
                          product={lookupProduct(
                            combo.IndoorManufacturer,
                            combo.IndoorModelNumber
                          )}
                        >
                          {[combo.IndoorManufacturer, combo.IndoorModelNumber]
                            .filter(Boolean)
                            .join(' ')}
                        </ModelSpecsTooltip>
                        <StockDot
                          product={lookupProduct(
                            combo.IndoorManufacturer,
                            combo.IndoorModelNumber
                          )}
                        />
                      </>
                    ) : (
                      [combo.IndoorManufacturer, combo.IndoorModelNumber]
                        .filter(Boolean)
                        .join(' ')
                    )}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {combo.BlowerModelNumber ? (
                      <>
                        <ModelSpecsTooltip
                          manufacturer={combo.BlowerManufacturer}
                          modelNumber={combo.BlowerModelNumber}
                          modelSpecs={modelSpecs}
                          product={lookupProduct(
                            combo.BlowerManufacturer,
                            combo.BlowerModelNumber
                          )}
                        >
                          {[combo.BlowerManufacturer, combo.BlowerModelNumber]
                            .filter(Boolean)
                            .join(' ')}
                        </ModelSpecsTooltip>
                        <StockDot
                          product={lookupProduct(
                            combo.BlowerManufacturer,
                            combo.BlowerModelNumber
                          )}
                        />
                      </>
                    ) : (
                      [combo.BlowerManufacturer, combo.BlowerModelNumber]
                        .filter(Boolean)
                        .join(' ')
                    )}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.SEER2)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.EER2)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.HSPF2)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.COP5)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.AFUE)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.CoolingCapacity)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {formatPerfValue(combo.HeatingCapacity)}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {combo.AHRICertificateNumber ?? '—'}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {(() => {
                      const resolved = parseAccessoriesString(combo.Accessories)
                        .map((p) => lookupAccessory(p.accessoryId, p.typeId))
                        .filter((e): e is EnrichedAccessory => e !== undefined);
                      if (resolved.length === 0) {
                        return <span className='text-gray-400'>—</span>;
                      }
                      return <AccessoriesTooltip entries={resolved} />;
                    })()}
                  </td>
                  <td className='whitespace-nowrap px-3 py-2 text-sm'>
                    {renderTotalPrice(combo)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Modal
      title='Compatible Combinations Browser'
      openState={openState}
      infoMode
      size='fullscreen'
    >
      {/* Existing equipment */}
      {components.length > 0 && (
        <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3'>
          <div className='mb-2 text-sm font-semibold text-gray-700'>
            Existing Equipment
          </div>
          <div className='flex flex-wrap gap-3'>
            {components.map((component) => {
              const config = component.type
                ? existingSystemComponentTypeProperties[component.type]
                : undefined;
              const matchingEntries = config
                ? Object.entries(config).filter(
                    ([, fieldConfig]) => fieldConfig.matching === true
                  )
                : [];
              const editing = editingComponentUniqueId === component.uniqueId;
              return (
                <div
                  key={component.uniqueId}
                  className='flex min-w-[220px] flex-col gap-2 rounded-md border border-gray-200 bg-white p-3'
                >
                  <div className='flex items-center justify-between gap-3'>
                    <span className='text-sm font-semibold'>
                      {component.type || 'Unknown'}
                    </span>
                    <div className='flex items-center gap-3'>
                      {!editing && (
                        <button
                          type='button'
                          className='text-xs font-medium text-blue-600 hover:text-blue-800'
                          onClick={() =>
                            setEditingComponentUniqueId(component.uniqueId)
                          }
                        >
                          Edit
                        </button>
                      )}
                      <label className='flex cursor-pointer select-none items-center gap-2 text-xs font-medium text-gray-700'>
                        <ToggleSwitch
                          value={component.replacing}
                          onChange={(value) => {
                            dispatch(
                              updateExistingSystemComponent({
                                ...component,
                                replacing: value
                              })
                            );
                            updateComponentReplacing({
                              uniqueId: component.uniqueId,
                              replacing: value
                            });
                          }}
                        />
                        {component.replacing ? 'Replacing' : 'Keeping'}
                      </label>
                    </div>
                  </div>
                  {editing ? (
                    <ExistingEquipmentComponentEditor
                      component={component}
                      onSaved={() => {
                        // The editor invokes this inside flushSync together
                        // with its Redux dispatch, so the fetch effect sees
                        // the nonce bump and the components-reference churn
                        // in ONE render — exactly one refetch per save.
                        setSpecsSaveNonce((n) => n + 1);
                        setEditingComponentUniqueId(null);
                      }}
                      onCancel={() => setEditingComponentUniqueId(null)}
                    />
                  ) : (
                    <>
                      {component.modelNumber && (
                        <div className='text-xs text-gray-500'>
                          Model: {component.modelNumber}
                        </div>
                      )}
                      {matchingEntries.length > 0 && (
                        <dl className='grid grid-cols-2 gap-x-3 gap-y-1 text-xs'>
                          {matchingEntries.map(([propKey, fieldConfig]) => (
                            <div key={propKey} className='flex flex-col'>
                              <dt className='text-gray-500'>
                                {fieldConfig.label}
                              </dt>
                              <dd className='font-medium text-gray-800'>
                                {formatSpecValue((component as any)[propKey])}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommended system types (AHA-6681) */}
      {fetchParams && (
        <RecommendationsStrip
          systemUniqueId={fetchParams.systemUniqueId}
          componentsKey={componentsKey}
          useRecommendations={useRecommendations}
          onToggleUseRecommendations={handleToggleUseRecommendations}
          onRecommendationsChange={handleRecommendationsChange}
          onRecommendationsPending={handleRecommendationsPending}
        />
      )}

      {/* Spec / performance filters (faceted) */}
      {filterOptions.length > 0 && (
        <div className='mb-4 rounded-lg border border-gray-200 bg-white p-3'>
          <div className='mb-2 flex items-center justify-between'>
            <div className='text-sm font-semibold text-gray-700'>Filters</div>
            {hasActiveFilters && (
              <button
                type='button'
                className='text-xs font-medium text-blue-600 hover:text-blue-800'
                onClick={() => {
                  // Clearing everything includes the band, so the toggle
                  // must drop too — otherwise ON would claim control of
                  // capacity inputs that no longer hold the band.
                  setFilters([]);
                  setUseStructureReqs(false);
                }}
              >
                Clear all
              </button>
            )}
          </div>
          {/* Structure requirements (AHA-6681): shown only when the
              structure has any BTU requirement; sits with the capacity
              filters it drives. */}
          {hasStructureReqs && (
            <div className='mb-3 flex flex-wrap items-center gap-4 rounded border border-gray-200 bg-gray-50 px-3 py-2'>
              <span className='text-xs font-medium text-gray-700'>
                Structure requires:{' '}
                {[
                  totalBTUs.heating > 0 &&
                    `${abbreviateNumber(
                      Math.round(totalBTUs.heating)
                    )} BTU heating`,
                  totalBTUs.cooling > 0 &&
                    `${abbreviateNumber(
                      Math.round(totalBTUs.cooling)
                    )} BTU cooling`
                ]
                  .filter(Boolean)
                  .join(' / ')}
              </span>
              <label className='flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600'>
                <ToggleSwitch
                  value={useStructureReqs}
                  onChange={handleToggleStructureReqs}
                />
                Use structure requirements
              </label>
            </div>
          )}
          <div className='flex flex-col gap-3'>
            {groupedOptions.map(({ group, options }) => {
              const controls = options
                .map((option) => renderFilterControl(option))
                .filter(Boolean);
              if (controls.length === 0) return null;
              return (
                <div key={group} className='flex flex-col gap-1'>
                  <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                    {group}
                  </div>
                  <div className='flex flex-wrap gap-x-6 gap-y-3'>
                    {controls}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* System type filter + active toggle. While the recommendations
          checkbox is on, the row narrows to the recommended types (hidden,
          not disabled) and drives `recommendedSubset` instead of the manual
          `systemTypeCodes`. Recommended codes absent from the facet (zero
          combinations in the universe) get no button — they'd have no label
          — but still ride along in the all-recommended (empty-subset) query,
          contributing zero rows. */}
      <div className='mb-4 flex flex-wrap items-center gap-2'>
        <button
          type='button'
          className={`rounded px-2 py-1 text-xs font-medium ${
            (
              useRecommendations
                ? recommendedSubset.length === 0
                : systemTypeCodes.length === 0
            )
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() =>
            useRecommendations
              ? setRecommendedSubset([])
              : setSystemTypeCodes([])
          }
        >
          All Types (
          {useRecommendations
            ? recommendedAllTypesCount(availableSystemTypes, recommendedCodes)
            : totalAvailable}
          )
        </button>
        {(useRecommendations
          ? availableSystemTypes.filter((st) =>
              recommendedCodes.includes(st.code)
            )
          : availableSystemTypes
        ).map((st) => (
          <button
            type='button'
            key={st.code}
            className={`rounded px-2 py-1 text-xs font-medium ${
              (
                useRecommendations
                  ? recommendedSubset.includes(st.code)
                  : systemTypeCodes.includes(st.code)
              )
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => {
              const toggle = (prev: string[]) =>
                prev.includes(st.code)
                  ? prev.filter((c) => c !== st.code)
                  : [...prev, st.code];
              if (useRecommendations) {
                setRecommendedSubset(toggle);
              } else {
                setSystemTypeCodes(toggle);
              }
            }}
          >
            {st.label} ({st.count ?? 0})
          </button>
        ))}
        <div className='ml-auto flex items-center gap-4'>
          <label className='flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600'>
            <ToggleSwitch
              value={inStockOnly}
              onChange={() => {
                setInStockOnly((prev) => !prev);
                setPageNumber(1);
              }}
            />
            In stock only
          </label>
          <label className='flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600'>
            <ToggleSwitch
              value={activeOnly}
              onChange={() => {
                setActiveOnly((prev) => !prev);
                setPageNumber(1);
              }}
            />
            Active only
          </label>
        </div>
      </div>

      {/* Table or loading/empty state */}
      {renderContent()}

      {/* Pagination */}
      <div className='mt-4 flex items-center justify-between'>
        <span className='text-sm text-gray-700'>
          {totalCount} result{totalCount !== 1 ? 's' : ''}
          {totalPages > 1 && ` — Page ${pageNumber} of ${totalPages}`}
        </span>
        <div className='flex items-center gap-2'>
          <select
            className='rounded border border-gray-300 px-2 py-1 text-sm'
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={9999}>All</option>
          </select>
          <Button
            text='Previous'
            onClick={() => setPageNumber((p) => p - 1)}
            disabled={pageNumber <= 1 || loading || combosDeferred}
            color='secondary'
          />
          <Button
            text='Next'
            onClick={() => setPageNumber((p) => p + 1)}
            disabled={pageNumber >= totalPages || loading || combosDeferred}
            color='secondary'
          />
        </div>
      </div>

      {/* Multi-select action bar — pins to the bottom of the modal's scroll
          viewport. -mx-6/px-6 extends the white background past the modal's
          p-6 padding so the bar looks like a true footer. */}
      {selectedCombinationIds.size > 0 && (
        <div className='sticky bottom-0 -mx-6 mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3'>
          <span className='text-sm font-medium'>
            {selectedCombinationIds.size} combination
            {selectedCombinationIds.size > 1 ? 's' : ''} selected
          </span>
          <button
            type='button'
            className='rounded bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700'
            onClick={() => beginAdd(selectedCombinations)}
          >
            Add to Quotable Systems
          </button>
        </div>
      )}

      <AccessorySelectionModal
        openState={accessorySelection.openState}
        combinationsWithChoices={accessorySelection.combinationsWithChoices}
        systemCount={accessorySelection.systemCount}
        onConfirm={accessorySelection.onConfirm}
      />
    </Modal>
  );
}
